import React, { useState, useEffect, Component } from 'react';
import { useAuth } from '../App';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, Users, FileText, Image, Flag, Ban, CheckCircle, XCircle, Search, Filter, Shield, Clock, UserPlus, UserMinus, ChevronDown, ChevronRight, Settings, BookOpen, Activity, Target, Layout, Sparkles, RefreshCw, LogOut, AlertCircle, Bug } from 'lucide-react';
import AdminVerificationsTab from '../components/AdminVerificationsTab';

// Error Boundary Component
class AdminErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Admin Dashboard Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-red-500/50 rounded-xl p-8 max-w-md text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-red-400 mb-2">Something went wrong</h2>
            <p className="text-zinc-400 mb-6">The admin dashboard encountered an error.</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
            >
              <RefreshCw className="w-4 h-4 inline mr-2" />
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Auth Error Card Component
const AuthErrorCard = ({ error, onRetry, onLogout }) => {
  const isAuthError = error?.response?.status === 401 || error?.response?.status === 403;
  
  return (
    <div className="bg-zinc-900 border border-red-500/30 rounded-xl p-6 text-center" data-testid="admin-auth-error">
      <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
      <h3 className="text-lg font-bold text-red-400 mb-2">
        {isAuthError ? 'Admin Session Expired or Insufficient Permissions' : 'Failed to Load Data'}
      </h3>
      <p className="text-zinc-400 mb-4">
        {isAuthError 
          ? 'Your session may have expired or you may not have admin access. Please log in again.'
          : error?.message || 'An error occurred while loading admin data.'}
      </p>
      <div className="flex gap-3 justify-center">
        <button
          onClick={onRetry}
          className="bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Retry
        </button>
        {isAuthError && (
          <button
            onClick={onLogout}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            Log Out & Sign In Again
          </button>
        )}
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [reports, setReports] = useState([]);
  const [reportsError, setReportsError] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersError, setUsersError] = useState(null);
  const [reportFilters, setReportFilters] = useState({ status: 'pending', page: 1 });
  const [userFilters, setUserFilters] = useState({ status: 'all', role: 'all', search: '', page: 1 });
  const [actionLoading, setActionLoading] = useState(false);

  const BACKEND_URL = '';  // Use relative URLs

  // Get auth token with fallback
  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No auth token found in localStorage');
    }
    return { Authorization: `Bearer ${token || ''}` };
  };

  const handleLogout = () => {
    if (logout) {
      logout();
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    navigate('/');
  };

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    // Check if user is admin
    if (!user || (user?.role !== 'admin' && !user?.is_admin)) {
      navigate('/');
      return;
    }

    loadStats();
  }, [user, navigate, authLoading]);

  useEffect(() => {
    if (activeTab === 'reports') {
      loadReports();
    } else if (activeTab === 'users') {
      loadUsers();
    }
    // Verifications tab is handled by its own component
  }, [activeTab, reportFilters, userFilters]);

  const loadStats = async () => {
    setStatsError(null);
    try {
      const response = await axios.get('/api/admin/stats', {
        headers: getAuthHeader()
      });
      setStats(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load stats:', error);
      setStatsError(error);
      setLoading(false);
    }
  };

  const loadReports = async () => {
    setReportsError(null);
    try {
      const response = await axios.get('/api/admin/reports', {
        params: reportFilters,
        headers: getAuthHeader()
      });
      setReports(response.data.reports || []);
    } catch (error) {
      console.error('Failed to load reports:', error);
      setReportsError(error);
    }
  };

  const loadUsers = async () => {
    setUsersError(null);
    try {
      const response = await axios.get('/api/admin/users', {
        params: userFilters,
        headers: getAuthHeader()
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to load users:', error);
      setUsersError(error);
    }
  };

  const handleReportAction = async (reportId, action, reason = '') => {
    setActionLoading(true);
    try {
      await axios.post(
        `/api/admin/reports/${reportId}/resolve`,
        { action, reason },
        { headers: getAuthHeader() }
      );
      loadReports();
      loadStats();
    } catch (error) {
      console.error('Failed to take action:', error);
      alert('Failed to take action on report');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBanUser = async (userId, reason) => {
    if (!reason || reason.trim() === '') {
      alert('Please provide a reason for banning this user');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(
        `/api/admin/users/${userId}/ban`,
        { reason },
        { headers: getAuthHeader() }
      );
      loadUsers();
      loadStats();
      alert('User banned successfully');
    } catch (error) {
      console.error('Failed to ban user:', error);
      alert(error.response?.data?.detail || 'Failed to ban user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnbanUser = async (userId) => {
    setActionLoading(true);
    try {
      await axios.post(
        `/api/admin/users/${userId}/unban`,
        {},
        { headers: getAuthHeader() }
      );
      loadUsers();
      loadStats();
      alert('User unbanned successfully');
    } catch (error) {
      console.error('Failed to unban user:', error);
      alert('Failed to unban user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspendUser = async (userId, reason, durationDays = 7) => {
    if (!reason || reason.trim() === '') {
      alert('Please provide a reason for suspending this user');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(
        `/api/admin/users/${userId}/suspend`,
        { reason, duration_days: durationDays },
        { headers: getAuthHeader() }
      );
      loadUsers();
      loadStats();
      alert(`User suspended for ${durationDays} days`);
    } catch (error) {
      console.error('Failed to suspend user:', error);
      alert(error.response?.data?.detail || 'Failed to suspend user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnsuspendUser = async (userId) => {
    setActionLoading(true);
    try {
      await axios.post(
        `/api/admin/users/${userId}/unsuspend`,
        {},
        { headers: getAuthHeader() }
      );
      loadUsers();
      loadStats();
      alert('User unsuspended successfully');
    } catch (error) {
      console.error('Failed to unsuspend user:', error);
      alert('Failed to unsuspend user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    if (!newRole) return;
    
    const confirmMessage = newRole === 'admin' 
      ? 'Are you sure you want to promote this user to ADMIN? They will have full platform control.'
      : `Are you sure you want to change this user's role to ${newRole}?`;
    
    if (!window.confirm(confirmMessage)) return;

    setActionLoading(true);
    try {
      await axios.patch(
        `/api/admin/users/${userId}/role`,
        { new_role: newRole },
        { headers: getAuthHeader() }
      );
      loadUsers();
      loadStats();
      alert(`User role changed to ${newRole}`);
    } catch (error) {
      console.error('Failed to change role:', error);
      alert(error.response?.data?.detail || 'Failed to change user role');
    } finally {
      setActionLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-xl">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <AdminErrorBoundary>
    <div className="min-h-screen bg-black text-white" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-orange-600 py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">

                <AlertTriangle className="w-8 h-8" />
                Admin Dashboard
              </h1>
              <p className="text-white/80 mt-2">Moderate content and manage the Trap Comix platform</p>
            </div>
            <Link
              to="/admin/site-manager"
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
            >
              <Settings className="w-5 h-5" />
              Site Manager
            </Link>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-zinc-900 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto">
            {['overview', 'reports', 'users', 'verifications'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-4 font-semibold capitalize transition-colors ${
                  activeTab === tab
                    ? 'bg-orange-500 text-white'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            <h2 className="text-2xl font-bold">Platform Statistics</h2>
            
            {/* Show error card if stats failed to load */}
            {statsError && (
              <AuthErrorCard 
                error={statsError} 
                onRetry={loadStats} 
                onLogout={handleLogout}
              />
            )}
            
            {/* Show stats if loaded successfully */}
            {stats && !statsError && (
              <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                icon={<Users className="w-6 h-6" />}
                title="Total Users"
                value={stats.total_users}
                color="blue"
                onClick={() => navigate('/admin/users')}
              />
              <StatCard
                icon={<Users className="w-6 h-6" />}
                title="Creators"
                value={stats.total_creators}
                color="purple"
                onClick={() => navigate('/admin/creators')}
              />
              <StatCard
                icon={<FileText className="w-6 h-6" />}
                title="Series"
                value={stats.total_series}
                color="green"
                onClick={() => navigate('/admin/series')}
              />
              <StatCard
                icon={<Image className="w-6 h-6" />}
                title="Artworks"
                value={stats.total_artworks}
                color="pink"
                onClick={() => navigate('/admin/artworks')}
              />
              <StatCard
                icon={<BookOpen className="w-6 h-6" />}
                title="Novels"
                value={stats.total_novels}
                color="indigo"
                onClick={() => navigate('/admin/novels')}
              />
              <StatCard
                icon={<Flag className="w-6 h-6" />}
                title="Pending Reports"
                value={stats.pending_reports}
                color="red"
                onClick={() => navigate('/admin/reports?status=pending')}
              />
              <StatCard
                icon={<Ban className="w-6 h-6" />}
                title="Banned Users"
                value={stats.banned_users}
                color="orange"
                onClick={() => navigate('/admin/users?status=banned')}
              />
              <StatCard
                icon={<Users className="w-6 h-6" />}
                title="Recent Signups (7d)"
                value={stats.recent_signups}
                color="cyan"
                onClick={() => navigate('/admin/users?filter=recent')}
              />
              <StatCard
                icon={<FileText className="w-6 h-6" />}
                title="Episodes"
                value={stats.total_episodes}
                color="yellow"
                onClick={() => navigate('/admin/series')}
              />
              <StatCard
                icon={<Shield className="w-6 h-6" />}
                title="Audit Logs"
                value="View"
                color="slate"
                onClick={() => navigate('/admin/audit-logs')}
              />
              <StatCard
                icon={<Activity className="w-6 h-6" />}
                title="Activation"
                value="Metrics"
                color="orange"
                onClick={() => navigate('/admin/metrics/activation')}
              />
              <StatCard
                icon={<Target className="w-6 h-6" />}
                title="Onboarding"
                value="CTR"
                color="blue"
                onClick={() => navigate('/admin/metrics/onboarding')}
              />
              <StatCard
                icon={<Layout className="w-6 h-6" />}
                title="Page Builder"
                value="Edit"
                color="pink"
                onClick={() => navigate('/admin/page-builder')}
              />
              <StatCard
                icon={<Sparkles className="w-6 h-6" />}
                title="Site Config"
                value="UI"
                color="purple"
                onClick={() => navigate('/admin/site-config')}
              />
              <StatCard
                icon={<AlertTriangle className="w-6 h-6" />}
                title="Alert Channels"
                value="Discord"
                color="indigo"
                onClick={() => navigate('/admin/alert-settings')}
              />
              <BugReportStatCard navigate={navigate} />
            </div>
              </>
            )}
            
            {/* Show empty state if no stats and no error */}
            {!stats && !statsError && (
              <div className="bg-zinc-900 rounded-xl p-8 text-center text-zinc-400">
                <RefreshCw className="w-8 h-8 mx-auto mb-4 animate-spin" />
                Loading statistics...
              </div>
            )}
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold">Content Reports</h2>
              <div className="flex gap-2">
                {['pending', 'actioned', 'dismissed', 'all'].map((status) => (
                  <button
                    key={status}
                    onClick={() => setReportFilters({ ...reportFilters, status, page: 1 })}
                    className={`px-4 py-2 rounded-lg capitalize ${
                      reportFilters.status === status
                        ? 'bg-orange-500 text-white'
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            {/* Show error card if reports failed to load */}
            {reportsError && (
              <AuthErrorCard 
                error={reportsError} 
                onRetry={loadReports} 
                onLogout={handleLogout}
              />
            )}

            {!reportsError && (
            <div className="space-y-4">
              {reports.length === 0 ? (
                <div className="bg-zinc-900 rounded-xl p-8 text-center text-zinc-400">
                  No reports found
                </div>
              ) : (
                reports.map((report) => (
                  <ReportCard
                    key={report.id}
                    report={report}
                    onAction={handleReportAction}
                    loading={actionLoading}
                  />
                ))
              )}
            </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center flex-wrap gap-4">
              <h2 className="text-2xl font-bold">User Management</h2>
              <div className="flex gap-2 flex-wrap">
                <select
                  value={userFilters.role}
                  onChange={(e) => setUserFilters({ ...userFilters, role: e.target.value, page: 1 })}
                  className="px-4 py-2 rounded-lg bg-zinc-800 text-white border border-zinc-700"
                >
                  <option value="all">All Roles</option>
                  <option value="basic_user">Basic Users</option>
                  <option value="creator">Creators</option>
                  <option value="admin">Admins</option>
                </select>
                <select
                  value={userFilters.status}
                  onChange={(e) => setUserFilters({ ...userFilters, status: e.target.value, page: 1 })}
                  className="px-4 py-2 rounded-lg bg-zinc-800 text-white border border-zinc-700"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="banned">Banned</option>
                </select>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={userFilters.search}
                    onChange={(e) => setUserFilters({ ...userFilters, search: e.target.value, page: 1 })}
                    className="pl-10 pr-4 py-2 rounded-lg bg-zinc-800 text-white border border-zinc-700 w-64"
                  />
                </div>
              </div>
            </div>

            {/* Show error card if users failed to load */}
            {usersError && (
              <AuthErrorCard 
                error={usersError} 
                onRetry={loadUsers} 
                onLogout={handleLogout}
              />
            )}

            {!usersError && (
            <div className="bg-zinc-900 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-zinc-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                        Joined
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-8 text-center text-zinc-400">
                          No users found
                        </td>
                      </tr>
                    ) : (
                      users.map((targetUser) => (
                        <UserRow
                          key={targetUser.id}
                          user={targetUser}
                          onBan={handleBanUser}
                          onUnban={handleUnbanUser}
                          onSuspend={handleSuspendUser}
                          onUnsuspend={handleUnsuspendUser}
                          onChangeRole={handleChangeRole}
                          loading={actionLoading}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            )}
          </div>
        )}

        {/* Verifications Tab */}
        {activeTab === 'verifications' && (
          <AdminVerificationsTab backendUrl={BACKEND_URL} />
        )}
      </div>
    </div>
    </AdminErrorBoundary>
  );
};

// Stat Card Component
const StatCard = ({ icon, title, value, color, onClick }) => {
  const colorClasses = {
    blue: 'from-blue-600 to-blue-500',
    purple: 'from-purple-600 to-purple-500',
    green: 'from-green-600 to-green-500',
    pink: 'from-pink-600 to-pink-500',
    red: 'from-red-600 to-red-500',
    orange: 'from-orange-600 to-orange-500',
    cyan: 'from-cyan-600 to-cyan-500',
    yellow: 'from-yellow-600 to-yellow-500',
    indigo: 'from-indigo-600 to-indigo-500',
    slate: 'from-slate-600 to-slate-500',
  };

  return (
    <button 
      onClick={onClick}
      className={`bg-gradient-to-br ${colorClasses[color]} rounded-xl p-6 shadow-lg w-full text-left transition-all hover:scale-[1.02] hover:shadow-xl active:scale-[0.98] cursor-pointer group`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-white/80 text-sm font-medium">{title}</p>
          <p className="text-3xl font-bold text-white mt-2">{value || 0}</p>
        </div>
        <div className="flex items-center gap-2 text-white/80">
          {icon}
          <ChevronRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
    </button>
  );
};

// Bug Report Card with live badge count
const BugReportStatCard = ({ navigate }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get('/api/admin/bug-reports/count', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCount(data.new_count || 0);
      } catch { /* silent */ }
    };
    fetchCount();
  }, []);

  return (
    <button
      onClick={() => navigate('/admin/bug-reports')}
      className="bg-gradient-to-br from-red-600 to-red-500 rounded-xl p-6 shadow-lg w-full text-left transition-all hover:scale-[1.02] hover:shadow-xl active:scale-[0.98] cursor-pointer group relative"
      data-testid="admin-bug-reports-card"
    >
      {count > 0 && (
        <span className="absolute -top-2 -right-2 bg-white text-red-600 text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-lg animate-pulse">
          {count}
        </span>
      )}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-white/80 text-sm font-medium">Bug Reports</p>
          <p className="text-3xl font-bold text-white mt-2">{count > 0 ? count : 'View'}</p>
        </div>
        <div className="flex items-center gap-2 text-white/80">
          <Bug className="w-6 h-6" />
          <ChevronRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
    </button>
  );
};

// Report Card Component
const ReportCard = ({ report, onAction, loading }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [actionReason, setActionReason] = useState('');

  return (
    <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <Flag className="w-5 h-5 text-red-500" />
            <span className="font-semibold capitalize">{report.type || 'Report'}</span>
            <span className="text-sm text-zinc-400">
              {report.target_type && `on ${report.target_type}`}
            </span>
          </div>
          <p className="text-zinc-300 mb-3">{report.reason || 'No reason provided'}</p>
          <div className="flex gap-4 text-sm text-zinc-400">
            <span>Reported by: {report.reporter_email || 'Anonymous'}</span>
            <span>
              {new Date(report.created_at).toLocaleDateString()}
            </span>
            {report.status && (
              <span className="capitalize px-2 py-1 bg-zinc-800 rounded">
                {report.status}
              </span>
            )}
          </div>
          {report.target_info && (
            <div className="mt-3 p-3 bg-zinc-800 rounded-lg text-sm">
              <strong>Target:</strong> {JSON.stringify(report.target_info)}
            </div>
          )}
        </div>
      </div>

      {report.status === 'pending' && (
        <div className="mt-4 pt-4 border-t border-zinc-800">
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => onAction(report.id, 'dismiss')}
              disabled={loading}
              className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              <XCircle className="w-4 h-4" />
              Dismiss
            </button>
            <button
              onClick={() => onAction(report.id, 'approve')}
              disabled={loading}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4" />
              Approve
            </button>
            <button
              onClick={() => {
                if (window.confirm('Remove this content? This action cannot be undone.')) {
                  onAction(report.id, 'remove');
                }
              }}
              disabled={loading}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              <Ban className="w-4 h-4" />
              Remove Content
            </button>
            <button
              onClick={() => {
                const reason = window.prompt('Enter reason for banning user:');
                if (reason) {
                  onAction(report.id, 'ban_user', reason);
                }
              }}
              disabled={loading}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              <Ban className="w-4 h-4" />
              Ban User
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// User Row Component - Simplified 3-role system: basic_user, creator, admin
const UserRow = ({ user, onBan, onUnban, onSuspend, onUnsuspend, onChangeRole, loading }) => {
  const [showRoleMenu, setShowRoleMenu] = useState(false);
  const isBanned = user.status === 'banned';
  const isSuspended = user.status === 'suspended';
  const isAdmin = user.role === 'admin' || user.is_admin;

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-500/20 text-red-400';
      case 'creator': return 'bg-orange-500/20 text-orange-400';
      case 'basic_user': return 'bg-zinc-500/20 text-zinc-400';
      default: return 'bg-zinc-500/20 text-zinc-400';
    }
  };

  const getRoleDisplayName = (role, creatorType) => {
    if (role === 'admin') return 'Admin';
    if (role === 'creator') {
      if (creatorType === 'artist') return 'Creator (Artist)';
      if (creatorType === 'author') return 'Creator (Author)';
      return 'Creator';
    }
    return 'Basic User';
  };

  const getStatusDisplay = () => {
    if (isBanned) {
      return (
        <div>
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
            Banned
          </span>
          {user.banned_reason && (
            <div className="text-xs text-zinc-500 mt-1" title={user.banned_reason}>
              Reason: {user.banned_reason.substring(0, 20)}...
            </div>
          )}
        </div>
      );
    }
    if (isSuspended) {
      return (
        <div>
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
            Suspended
          </span>
          {user.suspension_ends_at && (
            <div className="text-xs text-zinc-500 mt-1">
              Until: {new Date(user.suspension_ends_at).toLocaleDateString()}
            </div>
          )}
        </div>
      );
    }
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
        Active
      </span>
    );
  };

  return (
    <tr className="hover:bg-zinc-800/50">
      <td className="px-6 py-4">
        <div>
          <div className="font-medium text-white">{user.email}</div>
          {user.display_name && (
            <div className="text-sm text-zinc-400">{user.display_name}</div>
          )}
          {user.handle && (
            <div className="text-xs text-zinc-500">@{user.handle}</div>
          )}
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="relative">
          <button
            onClick={() => !isAdmin && setShowRoleMenu(!showRoleMenu)}
            disabled={isAdmin || loading}
            className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getRoleColor(user.role)} ${!isAdmin ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
          >
            {getRoleDisplayName(user.role, user.creator_type)}
            {!isAdmin && <ChevronDown className="w-3 h-3" />}
          </button>
          {showRoleMenu && !isAdmin && (
            <div className="absolute z-10 mt-1 w-40 bg-zinc-800 border border-zinc-700 rounded-lg shadow-lg py-1">
              {/* Simplified 3-role system */}
              {[
                { value: 'basic_user', label: 'Basic User' },
                { value: 'creator', label: 'Creator' },
                { value: 'admin', label: 'Admin' }
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => {
                    setShowRoleMenu(false);
                    if (value !== user.role) {
                      onChangeRole(user.id, value);
                    }
                  }}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-zinc-700 ${
                    value === user.role ? 'text-orange-400 font-medium' : 'text-white'
                  } ${value === 'admin' ? 'text-red-400' : ''}`}
                >
                  {label}
                  {value === user.role && ' ✓'}
                </button>
              ))}
            </div>
          )}
        </div>
      </td>
      <td className="px-6 py-4">
        {getStatusDisplay()}
      </td>
      <td className="px-6 py-4 text-sm text-zinc-400">
        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
      </td>
      <td className="px-6 py-4">
        {!isAdmin && (
          <div className="flex gap-2 flex-wrap">
            {/* Status Actions */}
            {isBanned ? (
              <button
                onClick={() => onUnban(user.id)}
                disabled={loading}
                className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm disabled:opacity-50 flex items-center gap-1"
              >
                <CheckCircle className="w-3 h-3" />
                Unban
              </button>
            ) : isSuspended ? (
              <button
                onClick={() => onUnsuspend(user.id)}
                disabled={loading}
                className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm disabled:opacity-50 flex items-center gap-1"
              >
                <CheckCircle className="w-3 h-3" />
                Unsuspend
              </button>
            ) : (
              <>
                <button
                  onClick={() => {
                    const reason = window.prompt('Enter reason for suspension:');
                    if (reason && reason.trim()) {
                      const days = window.prompt('Suspension duration (days):', '7');
                      if (days) {
                        onSuspend(user.id, reason, parseInt(days) || 7);
                      }
                    }
                  }}
                  disabled={loading}
                  className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white rounded text-sm disabled:opacity-50 flex items-center gap-1"
                  title="Temporarily suspend user"
                >
                  <Clock className="w-3 h-3" />
                  Suspend
                </button>
                <button
                  onClick={() => {
                    const reason = window.prompt('Enter reason for ban (permanent):');
                    if (reason && reason.trim()) {
                      onBan(user.id, reason);
                    }
                  }}
                  disabled={loading}
                  className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm disabled:opacity-50 flex items-center gap-1"
                  title="Permanently ban user"
                >
                  <Ban className="w-3 h-3" />
                  Ban
                </button>
              </>
            )}
          </div>
        )}
        {isAdmin && (
          <span className="text-xs text-zinc-500 flex items-center gap-1">
            <Shield className="w-3 h-3" />
            Admin user (protected)
          </span>
        )}
      </td>
    </tr>
  );
};

export default AdminDashboard;
